/**
 * Web notification LOCAL per-item "seen" overlay.
 *
 * DEMOTED to a local overlay (build-campaign 2026-07-09 · lane B · B1). The
 * durable read-state now lives SERVER-SIDE: `POST /v1/notifications/read`
 * (backend `notifications-read.ts`) stamps the caller's mark-all-read cursor
 * (`UserDoc.notificationsReadAt`), and `GET /v1/notifications` returns a
 * per-item `read` flag + `unreadCount` derived from that cursor — so "Mark all
 * read" survives a fresh session and syncs across devices. (This header
 * previously claimed no such endpoint existed; that was wrong.)
 *
 * What remains local: the backend model is a mark-ALL cursor with NO per-ITEM
 * dismiss endpoint, but the bell UX marks a single row as seen when the user
 * opens it. That per-item acknowledgement is what this store keeps — a
 * this-browser-only overlay persisted to localStorage (keyed on the feed's
 * stable synthetic id `${kind}:${entityId}`). The panel treats an item as read
 * when EITHER the server flag OR this overlay says so; the overlay never
 * substitutes for the server cursor.
 *
 * The store is deliberately tiny + dependency-free (no zustand): a module-level
 * Set + a subscriber list, surfaced to React via `useNotificationsReadState`
 * with `useSyncExternalStore`. It self-heals on a missing / unparseable
 * localStorage value and is a no-op in non-browser (SSR / jsdom-without-storage)
 * environments.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'elreign:notifications-read';
/** Cap the persisted set so it can't grow unbounded on a chatty account. The
 *  feed itself caps at ~100 items, so a few hundred remembered ids is ample;
 *  older ids beyond the cap are dropped (their items have long since resolved). */
const MAX_REMEMBERED = 500;

let readIds: Set<string> = loadFromStorage();
const subscribers = new Set<() => void>();
/** Cached immutable snapshot for useSyncExternalStore — only re-created when the
 *  set actually changes, so React's referential-equality bail-out works. */
let snapshot: ReadonlySet<string> = new Set(readIds);

function loadFromStorage(): Set<string> {
  if (typeof window === 'undefined' || !window.localStorage) return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function persist() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    // Keep only the most recent MAX_REMEMBERED ids (insertion order is preserved
    // by Set; slice the tail).
    const all = [...readIds];
    const trimmed = all.length > MAX_REMEMBERED ? all.slice(all.length - MAX_REMEMBERED) : all;
    if (trimmed.length !== all.length) readIds = new Set(trimmed);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...readIds]));
  } catch {
    /* storage full / disabled — best-effort, in-memory state still works */
  }
}

function emit() {
  snapshot = new Set(readIds);
  for (const fn of subscribers) fn();
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function getSnapshot(): ReadonlySet<string> {
  return snapshot;
}

/** Mark a single notification id as read (seen). No-op if already read. */
export function markNotificationRead(id: string): void {
  if (readIds.has(id)) return;
  readIds.add(id);
  persist();
  emit();
}

/**
 * Mark a batch of ids read in one go (the "Mark all read" action). Only emits
 * when something actually changed, so it's a no-op when everything is already
 * read.
 */
export function markNotificationsRead(ids: readonly string[]): void {
  let changed = false;
  for (const id of ids) {
    if (!readIds.has(id)) {
      readIds.add(id);
      changed = true;
    }
  }
  if (!changed) return;
  persist();
  emit();
}

/** React hook: the current set of read notification ids (re-renders on change). */
export function useNotificationsReadState(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only reset. Not exported through the barrel. */
export function __resetNotificationsReadStoreForTests(): void {
  readIds = new Set();
  snapshot = new Set();
  if (typeof window !== 'undefined' && window.localStorage) {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
  emit();
}
